/**
 * Work completion gate (#4101).
 *
 * Completing the last open labour step — or the last open service — rolls
 * the work order up to `work_completed` on the server
 * (`deriveWorkOrderStatus`). That is the moment the "Työ valmis?" dialog asks
 * for the odometer reading, the parking spot and the service book, so this
 * module decides whether an action is that moment.
 */

/** What the user is about to complete. */
export type WorkCompletionTarget =
  | { kind: 'labor_item'; serviceId: string; itemId: string }
  | { kind: 'service'; serviceId: string };

export interface WorkCompletionServiceSnapshot {
  bookingServiceId: string;
  status: string;
  laborItems?: readonly { id: string; status: string }[] | undefined;
}

/** Statuses from which the server derives `work_completed` when everything is done. */
const COMPLETABLE_WORK_ORDER_STATUSES: ReadonlySet<string> = new Set([
  'vehicle_arrived',
  'in_progress',
  'paused',
]);

/**
 * True when completing `target` leaves no open work on the order, i.e. the
 * server will move the work order to `work_completed`.
 *
 * - A labour step: every other step of its service is completed and every
 *   other service is completed.
 * - A service: every other service is completed.
 */
export function willCompleteWorkOrder(
  workOrderStatus: string,
  services: readonly WorkCompletionServiceSnapshot[],
  target: WorkCompletionTarget,
): boolean {
  if (!COMPLETABLE_WORK_ORDER_STATUSES.has(workOrderStatus)) return false;
  const targetService = services.find((service) => service.bookingServiceId === target.serviceId);
  if (!targetService || targetService.status === 'completed') return false;

  const othersDone = services.every(
    (service) => service.bookingServiceId === target.serviceId || service.status === 'completed',
  );
  if (!othersDone) return false;

  if (target.kind === 'service') return true;

  const items = targetService.laborItems ?? [];
  const item = items.find((entry) => entry.id === target.itemId);
  if (!item || item.status === 'completed') return false;
  return items.every((entry) => entry.id === target.itemId || entry.status === 'completed');
}
